import { API } from 'aws-amplify';
import { dashboardLlmAbnormalities } from '../../common/graphql/queries';
import { callQuery, LLM_DASHBOARD_QUERY_STATUS_BY_CSN } from './query';

const sxMockResponse = {
    "explanation_metadata": {
        "task": "sx_rx",
        "llm_model_used": "CLAUDE_37_SONNET",
        "max_current_visit_notes_limit": 10,
        "max_palliative_notes_limit": 15,
        "max_sx_rx_notes_limit": 15,
        "task_results": {
            "sx_rx": {
                "num_notes_provided": 3,
                "characters_sent": 19901,
                "characters_received": 2782,
                "characters_total": 22683
            }
        }
    },
    "sx_rx": {
        "error": "LLM response JSON missing required keys: ['patient_context', 'dyspnea', 'nausea', 'constipation', 'appetite', 'psychosocial', 'activity']",
        "patient_context": {},
        "pain": {
            "summary": "The patient reports generalized body aches since dialysis on Monday with mild intermittent left-sided chest pain, managed with acetaminophen as needed.",
            "symptoms": "The patient experiences generalized body aches that began after dialysis on Monday. She also reports mild intermittent left-sided chest pain. The pain appears to be acute rather than chronic based on the ED presentation.",
            "current_management": "The patient is prescribed acetaminophen (Tylenol Extra Strength) 500 mg tablets, taking 2 tablets (1,000 mg total) by mouth as needed for pain. No other specific pain medications or non-pharmacological approaches for pain management are documented.",
            "effectiveness": "Limited information is available regarding the effectiveness of the current pain management approach. The ED note indicates the patient was 'feeling better in ED w/o intervention,' suggesting some spontaneous improvement of symptoms without specific pain interventions during the ED visit.",
            "optimization_recommendations": "1. Consider scheduled acetaminophen (rather than as-needed) during periods of pain exacerbation. 2. Evaluate for potential dialysis-related factors contributing to body aches, such as electrolyte shifts or volume changes. 3. Consider non-pharmacological approaches such as heat therapy, gentle stretching, or physical therapy for generalized myalgia. 4. If pain persists or worsens, consider referral to pain management for comprehensive evaluation, especially given ESRD and medication limitations.",
            "evidence": [
                {
                    "date": "2025-02-25",
                    "citation": "ED Provider Notes",
                    "content": "Reports whole body aches and DOB since Monday after dialysis. More dyspnea on exertion. Not feeling SOB currently. States mild intermittent left sided chest pain. States she has pain like this"
                },
                {
                    "date": "2025-02-25",
                    "citation": "ED Provider Notes",
                    "content": "acetaminophen (Tylenol Extra Strength) 500 mg tablet Take 2 tablets (1,000 mg total) by mouth as needed for Pain, Historical Med"
                },
                {
                    "date": "2025-02-25",
                    "citation": "ED Provider Notes",
                    "content": "Pt feeling better in ED w/o intervention. Etiology of sxs not entirely clear but pt amenable to DC w/ strict return precautions."
                },
                {
                    "date": "2025-02-25",
                    "citation": "ED Provider Notes - Diagnosis",
                    "content": "Shortness of breath Myalgia"
                },
                {
                    "date": "2025-02-25",
                    "citation": "Discharge Instructions",
                    "content": "Take over-the-counter acetaminophen (Tylenol) as needed for pain or fever. You may take up to 1000 mg every 6 hours, for a maximum of 4000 mg every 24 hours."
                }
            ]
        },
        "dyspnea": {},
        "nausea": {},
        "constipation": {},
        "appetite": {},
        "psychosocial": {},
        "activity": {}
    }
};

const currentVisitMockResponse = {
    "explanation_metadata": {
        "task": "current_visit",
        "llm_model_used": "CLAUDE_37_SONNET",
        "max_current_visit_notes_limit": 10,
        "max_palliative_notes_limit": 15,
        "max_sx_rx_notes_limit": 15,
        "task_results": {
            "current_visit": {
                "num_notes_provided": 5,
                "characters_sent": 1604,
                "characters_received": 418,
                "characters_total": 2022
            }
        }
    },
    "current_visit": {
        "summary": "Presents with acute respiratory failure due to COPD exacerbation, requiring intubation and mechanical ventilation in the ED. Despite initial treatment with bronchodilators, steroids, and antibiotics, the patient's condition worsened with progressive hypoxemia and respiratory acidosis. Currently sedated and ventilated in the ICU with ongoing management of COPD exacerbation, with plans for extubation once stabilized."
    }
};

const palliativeMockResponse = {
    "explanation_metadata": {
        "task": "palliative",
        "llm_model_used": "CLAUDE_37_SONNET",
        "max_current_visit_notes_limit": 10,
        "max_palliative_notes_limit": 15,
        "max_sx_rx_notes_limit": 15,
        "task_results": {
            "palliative": {
                "num_notes_provided": 0,
                "characters_sent": 0,
                "characters_received": 0,
                "characters_total": 0
            }
        }
    },
    "palliative": {
        "error": "No relevant notes found"
    }
};

export const callDashboardLLM = async (input: any): Promise<string> => {
    const response: any = await API.graphql({
        query: dashboardLlmAbnormalities,
        variables: {
            input: JSON.stringify(input),
        }
    });
    // console.log('callDashboardLLM response', JSON.parse(response["data"]["dashboardLLMAbnormalities"]));
    return response["data"]["dashboardLLMAbnormalities"];

    // if (input["task"] === "current_visit") {
    //     return JSON.stringify(currentVisitMockResponse);
    // } else if (input["task"] === "palliative") {
    //     return JSON.stringify(palliativeMockResponse);
    // } else {
    //     return JSON.stringify(sxMockResponse);
    // }
}

export interface LLMDashboardStatus {
    csn: string;
    job: string;
    last_sync_timestamp: string;
    last_job_status: string;
    lock_timestamp: string;
    pending_event_types: string[];
}

export const callGetDashboardLLMStatus = async (csn: string): Promise<Record<string, LLMDashboardStatus>> => {
    const statuses = await callQuery({
        operation: LLM_DASHBOARD_QUERY_STATUS_BY_CSN,
        csn: csn,
    });

    const output: Record<string, LLMDashboardStatus> = {};
    for (let status of statuses) {
        output[status.job] = status;
    }
    return output;
}
